import { AuthContext, delay } from '@repo-level/core-front';
import { AddonsContext, isAuroraCrystalStartedLS } from 'contexts/addons';
import { playerLootboxActivation } from 'api/common';
import { ModalsContext, ModalType } from 'contexts/modal';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import { groupMember, USER_GROUP } from 'helpers/user';
import { useHandleBonus } from 'hooks/bonuses';
import { useLuckyWheel } from 'hooks/luckyWheel';
import { useCallback, useContext, useMemo } from 'react';
import { getSpinsData } from '../components/helpers';
import { useNavigation } from 'react-navi';

export enum ECrystalStatus {
  LOADING = 'LOADING',
  SPINNING = 'SPINNING',
  FINISHED = 'FINISHED',
}

const PROMO_CODE_1 = 'FORTUNE';
const PROMO_CODE_2 = 'AURORA';

export const useAuroraCrystalLogic = () => {
  const { handleModal } = useContext(ModalsContext);
  const { lootboxes, fetchLootboxes } = useContext(RealTimeServiceContext);
  const {
    crystalStatus: status,
    setCrystalStatus: setStatus,
    isAuroraCrystalStarted,
    setIsAuroraCrystalStarted,
    lootboxItem,
    setLootboxItem,
  } = useContext(AddonsContext);
  const { user, isAuthenticated } = useContext(AuthContext);

  const { lootbox } = useLuckyWheel();

  const { navigate } = useNavigation();

  const code = useMemo(() => {
    if (!user) return null;
    if (groupMember(USER_GROUP.BA)(user)) return PROMO_CODE_1;
    return PROMO_CODE_2;
  }, [user]);

  const { handleBonus } = useHandleBonus(code);

  const handleSpin = useCallback(async () => {
    if (!lootbox) return;

    setStatus(ECrystalStatus.SPINNING);
    const { data } = await playerLootboxActivation(lootbox.id);
    setLootboxItem(data);

    await delay(3000);
    setStatus(ECrystalStatus.FINISHED);
    fetchLootboxes && fetchLootboxes();
  }, [lootbox, setStatus, setLootboxItem, fetchLootboxes]);

  const spins = useMemo(() => {
    return getSpinsData(lootboxes || [], user);
  }, [lootboxes, user]);

  const handleButtonClick = useCallback(async () => {
    if (status === ECrystalStatus.FINISHED) {
      navigate('/bonuses?tab=active-offers');
      setTimeout(() => {
        setStatus(null);
        setLootboxItem(null);
      }, 1000);
      return;
    }

    if (spins.remains === 0 && lootboxItem === null) {
      navigate('/');
    }

    if (!isAuthenticated) {
      handleModal(ModalType.Registration)();
      return;
    }

    if (!isAuroraCrystalStarted) {
      setIsAuroraCrystalStarted(true);
      isAuroraCrystalStartedLS.push('true');
      return;
    }

    if (!lootbox) {
      handleBonus();
      return;
    }

    await handleSpin();
  }, [
    spins,
    lootboxItem,
    status,
    isAuroraCrystalStarted,
    isAuthenticated,
    lootbox,
    navigate,
    setStatus,
    setLootboxItem,
    setIsAuroraCrystalStarted,
    handleModal,
    handleBonus,
    handleSpin,
  ]);

  return {
    status,
    bonus: lootboxItem,
    spins,
    handleButtonClick,
  };
};

const MOCK_DATA = {
  weight: 18,
  prize: true,
  createdAt: '2025-11-03T08:51:00.147Z',
  bonuses: [
    {
      title: 'Wheel 25 FS',
      type: 'freespins',
      attributes: [
        {
          field: 'freespins_count',
          value: 25,
        },
        {
          field: 'freespins_max_win',
          value: [
            {
              currency: 'AUD',
              amountCents: 30000,
            },
            {
              currency: 'EUR',
              amountCents: 30000,
            },
            {
              currency: 'USD',
              amountCents: 30000,
            },
            {
              currency: 'BTC',
              amountCents: 1000000,
            },
            {
              currency: 'NZD',
              amountCents: 30000,
            },
            {
              currency: 'CAD',
              amountCents: 30000,
            },
            {
              currency: 'ETH',
              amountCents: 170000000,
            },
            {
              currency: 'NOK',
              amountCents: 300000,
            },
            {
              currency: 'LTC',
              amountCents: 322000000,
            },
            {
              currency: 'DOG',
              amountCents: 465300000000,
            },
            {
              currency: 'BCH',
              amountCents: 11300000000,
            },
          ],
        },
      ],
      resultBonus: [
        {
          field: 'wager',
          value: 35,
        },
        {
          field: 'max_win',
          value: [
            {
              currency: 'AUD',
              amountCents: 100000,
            },
            {
              currency: 'EUR',
              amountCents: 100000,
            },
            {
              currency: 'USD',
              amountCents: 100000,
            },
            {
              currency: 'BTC',
              amountCents: 3400000,
            },
            {
              currency: 'NZD',
              amountCents: 100000,
            },
            {
              currency: 'CAD',
              amountCents: 100000,
            },
            {
              currency: 'ETH',
              amountCents: 540000000,
            },
            {
              currency: 'NOK',
              amountCents: 1000000,
            },
            {
              currency: 'LTC',
              amountCents: 1111000000,
            },
            {
              currency: 'DOG',
              amountCents: 1300000000000,
            },
            {
              currency: 'BCH',
              amountCents: 420000000,
            },
          ],
        },
      ],
    },
  ],
  id: 915908,
};
